import { useContext, useEffect, useRef, useState } from "react"
import { TEmailAPIReports, TEmailAPIReportsMetric } from "./types"
import { EmailAPIContext } from "../context"
import Box from "@mui/material/Box"
import {
    Button,
    DataTable,
    EmailAPIFactory,
    formatNumber,
    GenericWrapperContext,
    LoadingContainer,
    startPromisePool,
    TDataTableColumnsData,
    TDataTableRowData,
    TOrder,
    TPromisePoolRef
} from "@cakemail-org/ui-components-v2"
import { useTranslation } from "react-i18next"
import classNames from "classnames"
import "./styles.scss"
import { affixToPercent, returnEmailTagsQuery } from "../../../utils/data"
import { getAPIDateRange } from "../../../utils/date"

export function EmailAPIReports({ }: TEmailAPIReports) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { timeRange, tagsFilter, search, emailTags, setEmailReportTagFilters, filterOperator } = useContext(EmailAPIContext)

    const { t, i18n } = useTranslation()
    const [metrics, setMetrics] = useState<TEmailAPIReportsMetric[]>()
    const emailReportsPoolRef = useRef<TPromisePoolRef>(undefined)

    const [order, setOrder] = useState<TOrder>("asc")
    const [orderBy, setOrderBy] = useState<string>("name")

    const [page, setPage] = useState<number>(1)

    const [loading, setLoading] = useState<boolean>(true)

    useEffect(() => {
        return () => {
            stopPromisePools()
        }
    }, [])

    useEffect(fetchMetrics, [timeRange, tagsFilter, emailTags?.data])

    useEffect(() => {
        setPage(1)
    }, [search])

    function stopPromisePools() {
        try {
            emailReportsPoolRef.current?.stop()
            emailReportsPoolRef.current = undefined
        } catch (e) { }
    }

    function fetchMetrics() {
        setLoading(true)
        stopPromisePools()
        const apiTimeRange = getAPIDateRange({ range: timeRange })
        if (emailTags?.data) {
            const emailTagArray = tagsFilter.length > 0 ? tagsFilter : emailTags.data

            startPromisePool<string, TEmailAPIReportsMetric | undefined>({
                finalSetOnly: true,
                data: emailTagArray,
                poolRef: emailReportsPoolRef,
                processor: ({ processSetter, item }) => {
                    return EmailAPIFactory.getReport({
                        tags: returnEmailTagsQuery(item, { operator: filterOperator }),
                        startTime: apiTimeRange.start,
                        endTime: apiTimeRange.end
                    }).then((data) => {
                        const dataItem = {
                            ...data[0],
                            id: item,
                            tag: item
                        }
                        processSetter(dataItem)
                        return dataItem
                    }).catch(() => { return undefined })
                },
                setter: (data: any) => {
                    const logs = Object.values(data) as TEmailAPIReportsMetric[]
                    const filteredDatra = logs.filter((l) => {
                        return tagsFilter.length > 0 ? tagsFilter.includes(l.id) : true
                    })

                    setMetrics(filteredDatra)
                    setLoading(false)
                }
            })

        }
    }

    const colsData: TDataTableColumnsData[] = [
        {
            id: "id",
            label: t("general-name"),
            width: 300,
            sortable: true,
        },
        {
            id: "delivered",
            label: t("general-emails-sent"),
            sortable: true,
        },
        {
            id: "unique_open",
            label: t("general-unique_open"),
            sortable: true,
        },
        {
            id: "unique_open_rate",
            label: t("general-unique_open_rate"),
            sortable: true,
        },
        {
            id: "unique_click",
            label: t("general-unique_click"),
            sortable: true,
        },
        {
            id: "unique_click_rate",
            label: t("general-unique_click_rate"),
            sortable: true,
        },
        {
            id: "actions",
            label: ""
        }
    ]

    let rows: TDataTableRowData[] = metrics?.map((r) => {

        return {
            id: r.id,
            delivered: formatNumber(r.delivered, genericWrapper.user?.language),
            unique_open: formatNumber(r.unique_open, genericWrapper.user?.language),
            unique_click: formatNumber(r.unique_click, genericWrapper.user?.language),
            unique_open_rate: affixToPercent(r.unique_open_rate, genericWrapper.user?.language),
            unique_click_rate: affixToPercent(r.clickthru_rate, genericWrapper.user?.language),
            actions: <Button onClick={() => {
                setEmailReportTagFilters({ or: [r.tag] })
            }} size="small" color="body1" variant="outlined">{t("general-view-report")}</Button>
        }
    }) || []

    rows = rows.filter((r) => {
        return r.id.includes(search)
    })

    const classnames = classNames("emailAPIReports", {
        "loading": loading
    })

    return <Box className={classnames} >
        <LoadingContainer loading={!metrics}>
            {metrics && <>
                <DataTable
                    initState={{ orderBy, order }}
                    columnsData={colsData}
                    rows={rows}
                    showPagination={false}
                />
            </>}
        </LoadingContainer>
    </Box>
}